import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { DataSourceSchema } from '../config/builder-schemas'
import { DATA_SOURCES as ETL_DATA_SOURCES } from '../config/builder-schemas'

interface DataSource {
  id: string
  source_id: string
  source_name: string
  source_type: string
  description: string
  category: string
  table_name: string | null
  ckan_resource_name: string | null
  available_fields: string[] | null
  schema_info?: { columns?: Array<{ name: string; type?: string }> } | null
  record_count: number | null
  is_active: boolean
}

const NUMERIC_DB_TYPES = new Set([
  'integer', 'int', 'int2', 'int4', 'int8', 'smallint', 'bigint',
  'numeric', 'decimal', 'real', 'double precision', 'float', 'float4', 'float8',
  'number', 'serial', 'bigserial'
])

function isNumericDbType(t: string | undefined): boolean {
  if (!t) return false
  return NUMERIC_DB_TYPES.has(t.toLowerCase())
}

/** Infiere si un campo es numérico por su nombre (ej. v_nitr2007, valor_2020) */
function inferNumericFromFieldName(fieldName: string): boolean {
  const name = fieldName.toLowerCase()
  if (/\d{4}/.test(name)) return true // contiene año
  if (/^v_/.test(name) || name.startsWith('valor') || name.includes('_valor')) return true
  if (/nitr|total|num_|cantidad|superficie|area|porcentaje|suma|avg|media|indice|ratio/.test(name)) return true
  return false
}

/**
 * Hook to load data sources dynamically from data_sources table
 * HYBRID MODE: Combines ETL sources (hardcoded) + CKAN sources (from database)
 */
export function useDataSources() {
  const [dataSources, setDataSources] = useState<DataSourceSchema[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDataSources()
  }, [])

  async function loadDataSources() {
    try {
      setLoading(true)
      setError(null)

      const { data, error: queryError } = await supabase
        .from('data_sources')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('source_name', { ascending: true })

      if (queryError) {
        throw queryError
      }

      // Transform data_sources to DataSourceSchema format
      const schemas: DataSourceSchema[] = (data || []).map((source: DataSource) => {
        // Determine icon based on category or type
        let icon = '📊' // default
        if (source.category === 'demografía') icon = '👥'
        else if (source.category === 'medio ambiente') icon = '🌿'
        else if (source.category === 'economía') icon = '💰'
        else if (source.category === 'cultura y turismo') icon = '🎭'
        else if (source.category === 'infraestructura') icon = '🏗️'
        else if (source.category === 'geografía') icon = '🗺️'
        else if (source.category === 'sistema') icon = '⚙️'
        else if (source.source_type.startsWith('ckan_')) icon = '📁'

        // Build tableName: for CKAN resources, use special syntax
        let tableName: string
        if (source.source_type.startsWith('ckan_')) {
          tableName = `ckan_data_generic[${source.ckan_resource_name}]`
        } else {
          tableName = source.table_name || source.source_id
        }

        // Build fields from available_fields; usar schema_info para tipo si existe
        const columnsByName = new Map<string, string>()
        if (source.schema_info?.columns) {
          source.schema_info.columns.forEach((col: { name: string; type?: string }) => {
            if (col.name && col.type) columnsByName.set(col.name, col.type)
          })
        }
        const fieldNames = Array.isArray(source.available_fields)
          ? source.available_fields
          : (source.available_fields && typeof source.available_fields === 'object' && !Array.isArray(source.available_fields))
            ? Object.keys(source.available_fields)
            : []
        const fields = fieldNames.map((fieldName: string) => {
          const dbType = columnsByName.get(fieldName)
          const isNumeric = isNumericDbType(dbType) || inferNumericFromFieldName(fieldName)
          return {
            name: fieldName,
            label: fieldName,
            type: (isNumeric ? 'number' : 'string') as 'number' | 'string',
            aggregatable: isNumeric,
            description: ''
          }
        })

        return {
          tableName,
          label: source.source_name,
          description: source.description || '',
          recordCount: source.record_count || 0,
          icon,
          fields
        }
      })

      // HYBRID MODE: Combine ETL sources (hardcoded) with dynamic sources from database
      // ETL sources come first (they're the established ones)

      // Filter out duplicates: if a source from DB has the same tableName as ETL, skip it
      const etlTableNames = new Set(ETL_DATA_SOURCES.map(s => s.tableName))
      const uniqueDynamicSources = schemas.filter(s => !etlTableNames.has(s.tableName))

      const combinedSources = [
        ...ETL_DATA_SOURCES,      // 8 ETL tables (existencias_forestales, etc.)
        ...uniqueDynamicSources   // Only unique dynamic sources (CKAN, etc.)
      ]

      

      setDataSources(combinedSources)

    } catch (err) {
      console.error('Error loading data sources:', err)
      setError(err instanceof Error ? err.message : 'Error desconocido')

      // FALLBACK: If database fails, at least show ETL sources
      setDataSources(ETL_DATA_SOURCES)
    } finally {
      setLoading(false)
    }
  }

  return {
    dataSources,
    loading,
    error,
    reload: loadDataSources
  }
}
